import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { GoogleAnalytics } from '~/google_analytics/events'
import { EventParameter } from '~/google_analytics/events.enum'

export function useScrollTracking(throttleTime: number = 200) {
    const route = useRoute()
    let maxScrolledPercent = 0
    let timeout: ReturnType<typeof setTimeout> | null = null

    const updateScrollPercent = () => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const docHeight = document.documentElement.scrollHeight - window.innerHeight
        if (docHeight <= 0) return

        const scrolledPercent = Math.floor((scrollTop / docHeight) * 100)
        if (scrolledPercent > maxScrolledPercent) {
            maxScrolledPercent = scrolledPercent
        }
    }

    const scrollHandler = () => {
        if (throttleTime > 0) {
            if (timeout) clearTimeout(timeout)
            timeout = setTimeout(updateScrollPercent, throttleTime)
        } else {
            updateScrollPercent()
        }
    }

    const sendScrollEvent = (pagePath: string = window.location.pathname) => {
        if (maxScrolledPercent > 0) {
            GoogleAnalytics.scroll({
                [EventParameter.PERCENT_SCROLL]: maxScrolledPercent,
                [EventParameter.PAGE_PATH]: pagePath,
            })
            maxScrolledPercent = 0 // reset after sending
        }
    }

    onMounted(() => {
        window.addEventListener('scroll', scrollHandler)
        window.addEventListener('beforeunload', () => sendScrollEvent())
    })

    onBeforeUnmount(() => {
        sendScrollEvent() // final call if layout unmounts
        window.removeEventListener('scroll', scrollHandler)
        if (timeout) clearTimeout(timeout)
    })

    // SPA route change
    watch(route, (newRoute, oldRoute) => {
        if (oldRoute.path) {
            // Send max scroll of the **previous page**
            sendScrollEvent(oldRoute.fullPath)
        }
    })
}
